<template>
   <div
    :class="{
      'card': true,
      'is-smallshadow': true,
      'complete': isFormComplete
    }">
    <div
      :class="{
        'card-body': true,
        'width-narrow': !readonly,
        'form-readonly': readonly
      }">
      <h2
        :class="{
          'heading-bigger mb-4': !readonly,
          'heading-small text-grey': readonly
        }">
        {{ $t('inquiryBankOffer.preferredBanksOffer') }}
      </h2>

      <div v-if="allDataLoaded">
        <div class="form-group">
          <label class="wide" for="selected-banks">{{ $t('inquiryBankOffer.doYouWantNowOffersBanks') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required"
              name="selected-banks"
              v-slot="{ errors }">
              <f-form-radio
                :value="true"
                v-model="data.selected_banks">
                {{ $t('inquiryBankOffer.onlyMyOwnChoose') }}
              </f-form-radio>
              <f-form-radio
                :value="false"
                v-model="data.selected_banks"
                v-pure-html="$t('inquiryBankOffer.allBanksOnList')">
              </f-form-radio>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            <template v-if="data.selected_banks">
              {{ $t('ui.yes') }}
            </template>
            <template v-else>
              {{ $t('ui.no') }}
            </template>
          </template>
        </div>

        <div
          v-if="!!data.selected_banks && banksLoaded"
          class="form-group all-banks">
          <label class="narrow" for="banks">{{ $t('inquiryBankOffer.selectedBanks') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="banks"
              v-slot="{ errors }">
              <f-checkbox
                class="block mt-1 mb-2"
                v-model="allBanksSelected"
                @change="selectAll()">
                {{ $t('inquiryBankOffer.selectAll') }}
              </f-checkbox>

              <div class="checkbox-multi-wrapper">
                <f-checkbox
                  v-for="(bank, index) of allBanks"
                  :key="index"
                  class="block"
                  v-model="data.banks[bank.id]"
                  @change="bankSelected(bank.id)">
                  {{ bank.name }}
                </f-checkbox>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </div>
            </validation-provider>
          </template>
          <template v-else>
            <div class="flex flex-wrap bank-items-wrapper">
              <span
                v-for="(bank, index) of banks"
                :key="index"
                class="bank-item mb-1">
                {{ bank.name }}
              </span>
            </div>
          </template>
        </div>
      </div>
      <div
        v-if="!allDataLoaded && !loadError"
        class="loading-spinner-wrapper">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'inquiry-bank-offer-form',
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isDuplicate: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: false
    },
    selectedBanks: {
      type: Boolean,
      default: null
    },
    banks: {
      type: Array,
      default: () => [],
      required: false
    },
  },
  computed: {
    isFormComplete() {
      let isComplete = false;

      if (this.data.selected_banks === false) {
        isComplete = true;
      } else if (this.data.selected_banks) {
        isComplete = Object.values(this.data.banks).some(item => item === true);
      }

      isComplete = isComplete && this.isValid;
      if (this.allDataLoaded && (isComplete !== this.previousIsCompleteSet)) {
        Vue.set(this, 'previousIsCompleteSet', isComplete);
        this.$bus.$emit('inquiry-form-part-complete', this.index, isComplete, 'bank-offer');
      }

      return isComplete;
    },
    allDataLoaded () {
      return this.loaded && this.banksLoaded;
    }
  },
  data() {
    return {
      data: {
        selected_banks: null,
        banks: {}
      },
      loaded: false,
      loadError: false,
      allBanksSelected: false,
      allBanks: [],
      banksLoaded: false,
      previousIsCompleteSet: false
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/banks/items',
        method: 'get',
        outputKey: 'allBanks',
        loadedKey: 'banksLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        },
        successAction: () => {
          Vue.nextTick(() => {
            this.data.banks = {};

            for (let i = 0; i < this.allBanks.length; i++) {
              Vue.set(this.data.banks, this.allBanks[i].id, false);
            }

            if (this.isEdit || this.isDuplicate) {
              Vue.set(this.data, 'selected_banks', this.selectedBanks);

              for (let i = 0; i < this.banks.length; i++) {
                Vue.set(this.data.banks, this.banks[i].id, true);
              }
            }
            if (this.selectedBanks && this.banks.length > 0 && this.banks.length === this.allBanks.length && Object.values(this.data.banks).every(item => item === true)) {
              Vue.set(this, 'allBanksSelected', true);
            }

            this.loaded = true;
          });
        },
      });
    },
    bankSelected () {
      Vue.nextTick(() => {
        if (Object.values(this.data.banks).length && Object.values(this.data.banks).every(item => item === true)) {
          Vue.set(this, 'allBanksSelected', true);

          return
        }

        Vue.set(this, 'allBanksSelected', false);
      });
    },
    selectAll () {
      Vue.nextTick(() => {
        if (!this.allBanksSelected) {
          Vue.set(this.data, 'banks', {});

          return;
        }

        let keys = this.allBanks.map(bank => bank.id);

        for (let i = 0; i < keys.length; i++) {
          Vue.set(this.data.banks, keys[i], true);
        }
      });
    },
    getData () {
      let banksToSave = Object.keys(this.data.banks).filter(key => this.data.banks[key]);

      return {
        selected_banks: this.data.selected_banks ? 1 : 0,
        banks: banksToSave
      };
    }
  }
}
</script>

<style scoped lang="scss">
.form-group.all-banks .f-checkbox {
  &:after {
    top: 10px;
  }

  &:before {
    top: 1px;
  }
}

.checkbox-multi-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;

  .f-checkbox {
    margin-bottom: 7px;
  }
}

.bank-items-wrapper {
  margin-right: -20px;
}

.bank-item {
  margin-right: 20px;
  width: calc((100% / 3) - 20px);
}

@media screen and (max-width: 1300px) {
  .bank-items-wrapper {
    margin-right: -20px;
  }

  .bank-item {
    margin-right: 20px;
    width: calc((100% / 2) - 20px);
  }
}

@media screen and (max-width: 720px) {
  .all-banks {
    flex-wrap: wrap;
  }

  .bank-items-wrapper {
    margin-top: 2rem;
    width: 100%;
  }
}

@media screen and (max-width: 560px) {
  .checkbox-multi-wrapper {
    grid-template-columns: 1fr;
  }

  .bank-items-wrapper {
    margin-right: 0;
  }

  .bank-item {
    margin-right: 0;
    width: 100%;
  }
}

</style>
